import type { NxHue } from '../lib/hue';

/** Key of a column or a card. Numbers and strings are both fine; they are compared with `===`. */
export type NxBoardKey = string | number;

/** Intent colour of a column's marker strip. `neutral` draws none. */
export type NxBoardTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/**
 * Colour of a marker strip (column) or accent edge (card): an intent tone when
 * the colour means something (overdue = danger), a category hue when it only
 * tells groups apart („Prüfen“ in violet).
 */
export type NxBoardAccent = NxBoardTone | NxHue;

const TONES: readonly string[] = ['neutral', 'info', 'success', 'warning', 'danger'];

/** CSS colour of an accent, `null` for none (`neutral` or unset). */
export function boardAccentColor(accent: NxBoardAccent | null | undefined): string | null {
  if (!accent || accent === 'neutral') return null;
  return TONES.includes(accent) ? `var(--nxt-${accent}-bg)` : `var(--nxt-hue-${accent})`;
}

export interface NxBoardColumn<T> {
  id: NxBoardKey;
  title: string;
  items: T[];
  tone?: NxBoardAccent;
  /** Highlights the column (today in a week view); also sets `aria-current="date"`. */
  current?: boolean;
  /**
   * Soft limit (WIP limit). Only displayed: the counter turns into a warning
   * once the column holds more cards. Refusing a drop is the caller's job
   * (`canMove`).
   */
  limit?: number;
  /** A disabled column accepts no drops and its cards cannot be picked up. */
  disabled?: boolean;
  /**
   * The column's total when it holds only a page of its cards (the server's
   * count). Omitted: the cards it holds. `null`: unknown — a dash, with
   * `countUnavailableLabel` for screen readers. The empty row shows only for
   * a total of 0 (or no total and no cards).
   */
  count?: number | null;
}

/** One requested move. `index` is the card's position in the target column AFTER the move. */
export interface NxBoardMove<T> {
  item: T;
  itemKey: NxBoardKey;
  from: NxBoardKey;
  to: NxBoardKey;
  /** Position the card had in its source column. */
  fromIndex: number;
  index: number;
}

/**
 * Turn a raw drop slot (the gap between cards in the target column as it is
 * rendered, i.e. still containing the dragged card) into the final index.
 *
 * Moving a card down inside its own column removes it above the slot first, so
 * every slot below its old place shifts up by one.
 */
export function resolveDropIndex(sameColumn: boolean, fromIndex: number, slot: number): number {
  if (sameColumn && fromIndex < slot) return slot - 1;
  return slot;
}

/** True when the move would leave the board exactly as it is. */
export function isNoopMove(
  move: Pick<NxBoardMove<unknown>, 'from' | 'to' | 'fromIndex' | 'index'>
) {
  return move.from === move.to && move.fromIndex === move.index;
}

/**
 * Apply a move to a column list and return a new list. The board itself never
 * mutates its columns — it only emits `move` — so this is the one-liner a
 * caller with local state needs. Callers that persist first and reload
 * afterwards do not need it.
 */
export function applyBoardMove<T>(
  columns: NxBoardColumn<T>[],
  move: NxBoardMove<T>,
  keyOf: (item: T) => NxBoardKey
): NxBoardColumn<T>[] {
  const source = columns.find((c) => c.id === move.from);
  const target = columns.find((c) => c.id === move.to);
  if (!source || !target) return columns;
  const fromIndex = source.items.findIndex((item) => keyOf(item) === move.itemKey);
  if (fromIndex < 0) return columns;
  const item = source.items[fromIndex];

  return columns.map((column) => {
    if (column.id !== source.id && column.id !== target.id) return column;
    let items = column.items;
    if (column.id === source.id) items = items.filter((_, i) => i !== fromIndex);
    if (column.id === target.id) {
      items = items.slice();
      const at = Math.max(0, Math.min(move.index, items.length));
      items.splice(at, 0, item);
    }
    return { ...column, items };
  });
}
